import {
  getAnonymousId,
  getDeviceCategory,
  getUserCountryCode
} from 'modules/meta/selectors'
import {getUrlParams} from 'modules/search/selectors'
import {createSelector} from 'reselect'
import {BookingManagementUrlParameters} from 'types/BookingManagementUrlParameters'
import {decorateOfferWithPayAtProperty} from 'utils/bookingManagement/decorateOfferWithPayAtProperty'

import {getCookie} from '@vio/core/src/_web/utils/cookies'
import {getQueryStringObject} from '@vio/core/src/_web/utils/url'
import {Price} from '@vio/core/src/api-types/bovio/response/booking'
import {Category} from '@vio/core/src/api-types/bovio/response/error'
import {getUser} from '@vio/core/src/auth/modules/selectors'
import {
  bookingRetrieveApi,
  GetBookingByIdRequest
} from '@vio/core/src/booking/services/bookingRetrieveApi'
import {RequestOtpRequest} from '@vio/core/src/booking/services/otpApi'
import {
  getRequestOtpResult,
  getVerifyOtpResult
} from '@vio/core/src/booking/services/otpApi/selectors'
import {getBovioApiKey} from '@vio/core/src/experiments/modules/selectors'
import {getTaxDisplayLogic} from '@vio/core/src/localization/business/countries'
import {defaultLanguage} from '@vio/core/src/localization/services/locale'
import {roomsToString} from '@vio/core/src/room/business/roomConfiguration'
import {updateAllPrices} from '@vio/core/src/room/business/transformSapiRoomsResponse/decoratePrices'

import type {RootState} from 'store'

const getStateParam = (state: RootState) => state
const getBookingManagementState = createSelector(
  [getStateParam],
  ({bookingManagement}) => bookingManagement
)

export const getBookingId = createSelector(
  [getUrlParams],
  ({bookingId}) => bookingId
)

export const getBookingManagementStage = createSelector(
  [getBookingManagementState],
  ({stage}) => stage
)

export const getBookingRetrieveQueryParams = createSelector(
  [getBookingId, getBovioApiKey, getAnonymousId, getDeviceCategory],
  (bookingId, profile, anonymousId, deviceType): GetBookingByIdRequest => {
    const {locale: urlLocale} =
      getQueryStringObject() as unknown as BookingManagementUrlParameters
    const locale = urlLocale || getCookie('locale') || defaultLanguage

    return {
      url: process.env.NEXT_PUBLIC_BOVIO_ENDPOINT!,
      bookingId,
      locale,
      profileKey: profile,
      httpRequestId: undefined,
      clientUserAgent: `booking-management/${process.env.NEXT_PUBLIC_APP_VERSION}`,
      anonymousId,
      deviceType
    }
  }
)

const getBookingRetrieveSelector = createSelector(
  [getBookingRetrieveQueryParams],
  params => bookingRetrieveApi.endpoints.getBookingById.select(params)
)

const getBookingRetrieveResult = createSelector(
  [getStateParam, getBookingRetrieveSelector],
  (state, bookingRetrieveSelector) => bookingRetrieveSelector(state)
)

export const getBookingRetrieve = createSelector(
  [getBookingRetrieveResult, getUserCountryCode],
  ({data: booking, ...rest}, userCountry) => {
    const {prices, checkIn, checkOut, rooms} = booking || {}
    const taxDisplayLogic = getTaxDisplayLogic(userCountry)
    return {
      data: booking
        ? decorateOfferWithPayAtProperty({
            ...booking,
            prices: prices.map(price =>
              updateAllPrices(
                price,
                {checkIn, checkOut, rooms: roomsToString(rooms)},
                taxDisplayLogic
              )
            ) as unknown as Price[]
          })
        : booking,
      ...rest
    }
  }
)

export const getOtpRequestQueryParams = createSelector(
  [getBookingId, getBovioApiKey],
  (bookingId, profile): RequestOtpRequest => {
    return {
      url: process.env.NEXT_PUBLIC_BOVIO_ENDPOINT!,
      bookingId,
      profileKey: profile,
      clientUserAgent: `booking-management/${process.env.NEXT_PUBLIC_APP_VERSION}`
    }
  }
)

export const getOtpRequestData = createSelector(
  [getRequestOtpResult],
  ({data, error}) => {
    if (data) return data
    // otp_cooldown data required to display otp verification step
    if (error && 'status' in error)
      return {
        ...(error?.data?.details || {}),
        maskedEmail: error?.data?.details?.reason?.maskedEmail,
        id: error?.data?.details?.reason?.lastOtpId,
        cooldown: error?.data?.details?.reason?.retryAfter
      }
    return null
  }
)

export const getBookingAccessMaskedEmail = createSelector(
  [getBookingRetrieveResult],
  ({error}) =>
    error && 'status' in error
      ? error.data?.details?.reason?.maskedEmail
      : undefined
)

export const getRetryOtpLimitReached = createSelector(
  [getRequestOtpResult],
  ({error}) =>
    !!error &&
    'status' in error &&
    error.data?.details?.category === Category.OtpRateLimited
)

export const getIncorrectOtpLimitReached = createSelector(
  [getVerifyOtpResult],
  ({error}) =>
    !!error &&
    'status' in error &&
    error.data?.details?.category === Category.OtpMaxAttemptsExceeded
)

export const getIsBookingOwner = createSelector(
  [getUser, getBookingRetrieve],
  (user, booking) =>
    Boolean(user?.attributes?.email) &&
    user.attributes.email === booking.data?.customer?.email
)
